import React from 'react';
import { Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { EmptyState } from '@components/ui/EmptyState';
import toast from 'react-hot-toast';

import { useAuth } from '@context/AuthContext';
import { rutaDeAterrizaje, nombreDelModulo } from '@/utils/navegacion';
import { SocketProvider } from '@context/SocketContext';
import { PortalClienteProvider } from '@context/PortalClienteContext';
import { PortalBrokerProvider } from '@context/PortalBrokerContext';
import { useProyectoStore } from '@/store/proyecto.store';
import AppLayout from '@/layouts/AppLayout';
import PortalLayout from '@/layouts/PortalLayout';
import { Spinner } from '@components/ui/Spinner';
import { ErrorBoundary } from '@components/ui/ErrorBoundary';

// ─── Entry points — carga inmediata ──────────────────────────
import Login from '@/pages/Login';
import EstablecerPassword from '@/pages/EstablecerPassword';
import SimuladorPublico from '@/pages/portal/SimuladorPublico';
import Encuesta from '@/pages/Encuesta';

// ─── Portales — lazy loading (split en chunks por ruta) ──────
const LoginPortalCliente      = React.lazy(() => import('@/pages/portal/LoginPortalCliente'));
const PortalClienteDashboard  = React.lazy(() => import('@/pages/portal/PortalClienteDashboard'));
const LoginPortalBroker       = React.lazy(() => import('@/pages/portal/LoginPortalBroker'));
const PortalBrokerDashboard   = React.lazy(() => import('@/pages/portal/PortalBrokerDashboard'));

// ─── Páginas ERP — lazy loading (split en chunks por ruta) ───
const DashboardShell = React.lazy(() => import('@/pages/DashboardShell'));
const Obra           = React.lazy(() => import('@/pages/Obra'));
const TableroProyectos = React.lazy(() => import('@/pages/dashboard/TableroProyectos'));
const CasaDelProyecto = React.lazy(() => import('./pages/CasaDelProyecto'));
const TableroPreview = React.lazy(() => import('@/pages/dashboard/TableroPreview')); // DEV-ONLY preview sin auth
const Notificaciones = React.lazy(() => import('@/pages/Notificaciones'));
const Proyectos      = React.lazy(() => import('@/pages/Proyectos'));
const Bancos         = React.lazy(() => import('@/pages/Bancos'));
const Tareas         = React.lazy(() => import('@/pages/Tareas'));
const Chat           = React.lazy(() => import('@/pages/Chat'));
const Workspace      = React.lazy(() => import('@/pages/Workspace'));
const Reportes       = React.lazy(() => import('@/pages/Reportes'));
const Configuracion  = React.lazy(() => import('@/pages/Configuracion'));
const Contabilidad   = React.lazy(() => import('@/pages/Contabilidad'));
const Overhead       = React.lazy(() => import('@/pages/Overhead'));
const EgresosPendientes = React.lazy(() => import('@/pages/EgresosPendientes'));

import { tieneAccesoAModulo } from '@/utils/accesoModulos';

const TOKEN_KEY = 'erp_token';

// ─── Portal Guards ────────────────────────────────────────────
function RequirePortalCliente({ children }: { children: React.ReactNode }) {
  return localStorage.getItem('portal_cliente_token')
    ? <>{children}</>
    : <Navigate to="/portal/cliente/acceso" replace />;
}

function RequirePortalBroker({ children }: { children: React.ReactNode }) {
  return localStorage.getItem('portal_broker_token')
    ? <>{children}</>
    : <Navigate to="/portal/broker/acceso" replace />;
}

// ─── Aterrizaje (I-251) ────────────────────────────────────────
// La primera pantalla es la lista de Proyectos; quien NO ve ese módulo cae en la primera entrada que sí ve
// (`rutaDeAterrizaje`) en vez de rebotar con un aviso rojo en cada entrada.
function Aterrizaje() {
  const { usuario, isLoading } = useAuth();
  if (isLoading) return <div className="flex items-center justify-center min-h-screen bg-rm-bg"><Spinner size="lg" color="blue" /></div>;
  if (!usuario) return <Navigate to="/login" replace />;
  return <Navigate to={rutaDeAterrizaje({ nivel: usuario.nivel, modulos: usuario.modulos ?? [], accesos: usuario.accesos })} replace state={{ aterrizaje: true }} />;
}

// ─── Route Guard ──────────────────────────────────────────────
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-rm-bg">
        <Spinner size="lg" color="blue" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// 🔇 Estos dos guardias devolvían al usuario al tablero SIN DECIRLE NADA. Medido abriendo
//    la pantalla como vendedor el 2026-09-03: entrar a /egresos lo dejaba en /dashboard, sin
//    una sola palabra — se queda creyendo que la pantalla no existe o que el sistema falló,
//    y acaba pidiéndole a otro que le mande el dato por WhatsApp. Regla de la casa: ninguna
//    acción termina muda, y menos el camino de ERROR, que es cuando la persona necesita
//    saber qué hacer. El `id` evita que un doble render apile dos avisos iguales.
function RedirigirConAviso({ aviso }: { aviso: string }) {
  React.useEffect(() => { toast(aviso, { icon: '🔒', id: aviso, duration: 5000 }); }, [aviso]);
  return <Navigate to="/dashboard" replace />;
}

function RequireNivel({ minNivel, children }: { minNivel: number; children: React.ReactNode }) {
  const { usuario } = useAuth();
  const nivel = usuario?.nivel ?? 0;
  if (nivel >= minNivel) {
    return <>{children}</>;
  }
  return <RedirigirConAviso aviso="Esa pantalla pide un nivel de acceso mayor al tuyo. Te dejamos en tu tablero — pídesela a tu responsable de área." />;
}

// 🔇🏠 PANTALLA MUDADA A LA CASA — avisa Y lleva al lugar correcto (BUG-1613, 2026-09-14).
//
// Hasta hoy había DOS medios patrones y ninguno completo: `RedirigirConAviso` (arriba) avisa
// pero siempre manda al tablero, y `RedirigirVentasApagada` (abajo) lleva a la casa pero se va
// MUDO. Las pantallas mudadas necesitan las dos cosas, y `/ingresos` y `/creditos` no tenían
// ninguna: caían en el catch-all `path="*"` → `/dashboard`, en silencio.
//
// 🔴 Lo que costaba, MEDIDO el 14-sep abriendo el DOM que el arnés capturó al fallar
// (`error-context.md` de los 3 caminos de `cobro-que-se-ve`): el usuario pedía `/ingresos` y
// aparecía en el Dashboard — sin una palabra. Y NO es sólo el arnés: el buscador ⌘K del ERP
// (`CommandPalette.tsx`) seguía ofreciendo «Ingresos» y su acción de cobro apuntando ahí. Un
// usuario real que escribiera «cobro» y diera Enter terminaba en el tablero creyendo que el
// sistema falló. Regla de la casa: ninguna acción termina muda.
//
// 🔴 Y conserva la querystring A PROPÓSITO: un deep-link trae `?ref=<id>` para abrir un
// registro concreto, y la casa sí lo respeta (`CasaDelProyecto.tsx:76` — al escribir `tab`
// «sólo se toca `tab`», lo demás se conserva). Perder el `?ref=` aquí dejaría al usuario en la
// pestaña correcta pero sin el registro que venía a ver: el mismo silencio, más disimulado.
// El `tab` viejo de la pantalla apagada (p. ej. `?tab=cuotas`) NO sobrevive: no existe en la
// casa y la mandaría a su pestaña por defecto.
function RedirigirPantallaApagada({ tab, aviso, avisoSinProyecto }: {
  tab: string; aviso: string; avisoSinProyecto: string;
}) {
  const contextoGuardado = useProyectoStore((s) => s.proyectoContextoId);
  const [searchParams] = useSearchParams();
  // I-251: el enlace viejo del Tablero trae `?proyecto=<id>` (`parts.tsx` buildUrl) — ése manda sobre el contexto guardado.
  const proyectoDelEnlace = searchParams.get('proyecto');
  const proyectoContextoId = proyectoDelEnlace || contextoGuardado;
  // 🔴 DOS textos, uno por rama — lo cazó la revisión adversarial (3 lentes, por separado).
  // Antes había uno solo, y en la rama sin proyecto le prometía al usuario «te llevamos a la
  // pestaña del proyecto en el que estás» mientras lo dejaba en el SELECTOR de proyectos, sin
  // proyecto y sin pestaña. Un aviso que miente es peor que el silencio que vino a cerrar:
  // el mudo deja a la persona buscando, el falso la deja creyendo que ya llegó.
  const texto = proyectoContextoId ? aviso : avisoSinProyecto;
  React.useEffect(() => { toast(texto, { icon: '🏠', id: texto, duration: 6000 }); }, [texto]);

  if (!proyectoContextoId) return <Navigate to="/proyectos" replace />;

  const query = new URLSearchParams(searchParams);
  query.delete('proyecto');
  query.set('tab', tab);
  return <Navigate to={`/proyectos/${proyectoContextoId}?${query.toString()}`} replace />;
}

// 🔴🚫 `/ventas` SE APAGÓ — Mau, 2026-09-12 14:19, textual: «APAGA LA PANTALLA VIEJA».
// `pages/Ventas.tsx` se movió a `pages/_apagadas/Ventas.tsx` (no se borró). Se vende
// desde la unidad (`casa/unidades/FichaDeUnidad.tsx`); las reglas de descuento ya
// viven en Config por proyecto (`ConfigColumna.tsx`); kanban/clientes/comisiones/
// brokers/leads ya se habían mudado a la casa. Esto sustituye, SÓLO para Ventas, a
// D-2026-09-07-F («lo pasado se queda»).
//
// Mismo molde que `Sidebar.tsx:192-195` (D-2026-09-07-F): con proyecto de contexto,
// entra directo a SU casa; sin él, al selector de proyectos.
//
// ✅ CORREGIDO 2026-09-12 (Mau, 14:44: «sí arreglar») — SÍ aterriza en "Unidades".
// `CasaDelProyecto.tsx` ya lee `?tab=` (arregla el hallazgo que este mismo comentario
// reportaba: guardaba su pestaña en `useState('pagos')` sin leer querystring). Se vende
// desde la unidad ⇒ el destino lógico de "Ventas apagada" es Unidades, no el default.
// 🔴 BUG-1613 (14-sep) — `RedirigirVentasApagada` se BORRÓ: llevaba a la casa pero se iba
// MUDA, y además tiraba la querystring (armaba la URL de cero, así que un `?ref=` del ⌘K se
// perdía). Era la 3ª casa del mismo defecto y se había dejado fuera «porque ya funcionaba».
// Regla dura de Mau: *ninguna acción termina muda, y arreglarlo NO se pregunta*. Ahora usa
// el mismo componente que Ingresos y Créditos — una sola pieza, no tres medias.

function RequireModule({ modulo, children }: { modulo: string; children: React.ReactNode }) {
  const { usuario } = useAuth();
  const nivel = usuario?.nivel ?? 0;
  const modulos = usuario?.modulos ?? [];
  // 🔴 La comparación vive en `utils/accesoModulos`, no aquí. Aquí decía
  //    `modulos.includes(modulo)`, que distingue mayúsculas: la base contesta 'Proyectos' y
  //    esta ruta preguntaba por 'proyectos', así que TODO usuario que no fuera N4 quedaba
  //    fuera de casi todo el ERP mientras el backend lo dejaba pasar. Medido el 4-sep
  //    abriendo la pantalla. El menú tenía la misma comparación por su cuenta: por eso ahora
  //    los dos llaman a la misma función y no pueden desincronizarse.
  if (tieneAccesoAModulo(modulos, modulo, nivel, usuario?.accesos)) {
    return <>{children}</>;
  }
  return <RedirigirConAviso aviso={`Tu usuario no tiene acceso a ${nombreDelModulo(modulo)}. Te dejamos en tu tablero — pídeselo a tu responsable de área.`} />;
}

// ─── Layout wrapper que incluye SocketProvider ─────────────────
function ProtectedLayout() {
  const token = localStorage.getItem(TOKEN_KEY);
  return (
    <SocketProvider token={token}>
      <AppLayout />
    </SocketProvider>
  );
}

const PageFallback = (
  <div className="flex items-center justify-center min-h-screen bg-rm-bg">
    <Spinner size="lg" color="blue" />
  </div>
);

// Combina Suspense + ErrorBoundary para cada ruta lazy.
// Si el chunk falla (deploy nuevo, hash mismatch), ErrorBoundary
// detecta el ChunkLoadError y recarga la página automáticamente.
// I-250: la pantalla suelta de Post-venta se apagó; quien llegue por un enlace viejo lo lee, no cae callado en otra parte.
export function PostventaSeMudo() {
  const irA = useNavigate();
  return (
    <EmptyState
      title="Post-venta se está mudando a cada proyecto"
      description="Por ahora no tiene pantalla propia. Cuando termine la mudanza la vas a encontrar dentro de cada proyecto."
      action={{ label: 'Ir a Proyectos', onClick: () => irA('/proyectos') }}
    />
  );
}

function LazyRoute({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <React.Suspense fallback={PageFallback}>
        {children}
      </React.Suspense>
    </ErrorBoundary>
  );
}

// ─── App ──────────────────────────────────────────────────────
const App: React.FC = () => {
  return (
    <Routes>
      {/* Raíz → lista de Proyectos (I-251, Mau 1-oct «4.- si»): la puerta de entrada es el proyecto, no el resumen. */}
      <Route path="/" element={<Aterrizaje />} />

      {/* Login (sin auth) */}
      <Route path="/login" element={<Login />} />

      {/* BUG-629: enlace de un solo uso para establecer contraseña — sin auth */}
      <Route path="/establecer-password/:token" element={<EstablecerPassword />} />

      {/* Encuesta de satisfacción — pública, sin login */}
      <Route path="/encuesta/:tenant/:token" element={<Encuesta />} />

      {/* Portal Cliente */}
      <Route
        path="/portal/cliente"
        element={
          <PortalClienteProvider>
            <PortalLayout titulo="Portal Cliente" />
          </PortalClienteProvider>
        }
      >
        <Route path="acceso" element={<LazyRoute><LoginPortalCliente /></LazyRoute>} />
        <Route
          path="dashboard"
          element={
            <RequirePortalCliente>
              <LazyRoute><PortalClienteDashboard /></LazyRoute>
            </RequirePortalCliente>
          }
        />
        <Route index element={<Navigate to="acceso" replace />} />
      </Route>

      {/* Portal Broker */}
      <Route
        path="/portal/broker"
        element={
          <PortalBrokerProvider>
            <PortalLayout titulo="Portal Broker" colorPrimario="#7C3AED" />
          </PortalBrokerProvider>
        }
      >
        <Route path="acceso" element={<LazyRoute><LoginPortalBroker /></LazyRoute>} />
        <Route
          path="dashboard"
          element={
            <RequirePortalBroker>
              <LazyRoute><PortalBrokerDashboard /></LazyRoute>
            </RequirePortalBroker>
          }
        />
        <Route index element={<Navigate to="acceso" replace />} />
      </Route>

      {/* Simulador Público — embeddable */}
      <Route path="/simulador/:proyecto_id" element={<SimuladorPublico />} />

      {/* DEV-ONLY: preview del Tablero de Proyectos (Fase 0) sin login ni backend. Temporal. */}
      <Route path="/tablero-preview" element={<LazyRoute><TableroPreview /></LazyRoute>} />

      {/* 🔴 LA CASA DEL PROYECTO va FUERA del AppLayout, a propósito.
          Mau: *«el sidebar es pequeño y ya no debería de tener tanta sección, ya que ahora
          es contenedor por proyecto»*. Esta pantalla ocupa el alto completo y trae su
          propio riel de 52px; meterla dentro del layout de 240px daría dos menús, que es
          justo el injerto que hubo que deshacer. Sigue protegida por login y por módulo. */}
      <Route
        path="/proyectos/:id"
        element={
          <RequireAuth>
            <RequireModule modulo="proyectos">
              <LazyRoute><CasaDelProyecto /></LazyRoute>
            </RequireModule>
          </RequireAuth>
        }
      />

      {/* Rutas protegidas con AppLayout + Socket.io */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <ProtectedLayout />
          </RequireAuth>
        }
      >
        <Route path="dashboard"       element={<LazyRoute><DashboardShell /></LazyRoute>} />
        <Route path="tablero"         element={<RequireNivel minNivel={3}><LazyRoute><TableroProyectos /></LazyRoute></RequireNivel>} />
        {/* I-56 (2026-09-11): la pantalla vieja `/ingresos` (pages/Ingresos.tsx, panel
            lateral incluido) se apaga aquí — la pestaña Ingresos de la casa
            (`IngresosDelProyectoTab`) alcanza todo lo que hacía. No se borra el archivo:
            se movió a `pages/_apagadas/`.
            🔴 BUG-1613 (2026-09-14): aquí decía «Sin redirección propia … cae en el catch-all
            de abajo → `/dashboard`», y ESO era el defecto: el usuario pedía Ingresos y
            aparecía en el tablero sin una palabra. Ahora lleva a la pestaña Ingresos de la
            casa y lo DICE. */}
        <Route path="ingresos"        element={<RedirigirPantallaApagada tab="ingresos" aviso="Los ingresos ahora viven dentro de cada proyecto. Te llevamos a la pestaña Ingresos del proyecto en el que estás." avisoSinProyecto="Los ingresos ahora viven dentro de cada proyecto. Elige el proyecto para verlos." />} />
        {/* I-04 (2026-09-16, Mau, «APAGA LO VIEJO DE UNA VEZ») — la pantalla vieja `/egresos`
            (pages/Egresos.tsx, 471 líneas) se apagó — la pestaña Pagos de la casa
            (`PagosDelProyectoTab`) alcanza todo lo que hacía: Registrar/CXP sistema/CXP portal
            proveedor (filtro «Portal de proveedores»), Recurrentes («Gastos que se repiten»),
            Cierre de mes («Revisar el mes» / «Cerrar el mes») y el
            panel de detalle (`EgresoDetalle`, componente COMPARTIDO — mismo código, misma
            fila). No se borró el archivo: se movió a `pages/_apagadas/`, mismo molde que
            Ingresos/Créditos/Ventas. Ver `qa/dia-2026-09-16/HANDOFF-AUDITOR-I-04-apagar-egresos-2026-09-16.md`
            para la tabla de actos completa (qué SÍ cubre, qué se dejó fuera y por qué).

            I-04-bis (2026-09-16, BUG-1750, D-2026-09-16-K) — el `/egresos` recuperó pantalla
            propia: la BANDEJA GLOBAL de pendientes por autorizar. Mau: «SI ASI ESTA BIEN una
            bandeja global de pendientes por autorizar». Es la mitad cross-proyecto que la
            pestaña Pagos, por diseño por-proyecto, no podía cubrir. Reusa `EgresoDetalle.tsx`
            TAL CUAL (mismo componente que Pagos) y el endpoint `GET /egresos` ya existente
            (ya aplica `obtenerProyectosVisibles()` server-side — no se reimplementa
            visibilidad aquí). Ver `qa/dia-2026-09-16/HANDOFF-AUDITOR-I-04-BIS-bandeja-egresos-pendientes.md`. */}
        <Route path="egresos"         element={<RequireModule modulo="egresos"><LazyRoute><EgresosPendientes /></LazyRoute></RequireModule>} />
        <Route path="bancos"          element={<RequireModule modulo="bancos"><LazyRoute><Bancos /></LazyRoute></RequireModule>} />
        <Route path="proyectos"       element={<RequireModule modulo="proyectos"><LazyRoute><Proyectos /></LazyRoute></RequireModule>} />
        <Route path="proyectos/*"     element={<RequireModule modulo="proyectos"><LazyRoute><Proyectos /></LazyRoute></RequireModule>} />
        {/* I-CRÉDITOS (2026-09-08, Tarea 8): la pantalla vieja `/creditos` (pages/Creditos.tsx,
            463 líneas) se apaga aquí — la 8ª pestaña de la casa del proyecto (SeccionCreditos)
            alcanza lo que ella hacía: lista+detalle+alta de créditos por proyecto y, sólo para
            el CEO, socios/aportaciones/distribución. No se borra el archivo — sigue existiendo
            sin ruta que lo monte, igual que el resto de mudanzas de la casa.
            🔴 BUG-1613: su hermana. El comentario de `/ingresos` de arriba la nombraba
            («mismo criterio que Créditos») — el mismo silencio, el mismo arreglo. */}
        <Route path="creditos"        element={<RedirigirPantallaApagada tab="creditos" aviso="Los créditos ahora viven dentro de cada proyecto. Te llevamos a la pestaña Créditos del proyecto en el que estás." avisoSinProyecto="Los créditos ahora viven dentro de cada proyecto. Elige el proyecto para verlos." />} />
        {/* 🔴 APAGADA 2026-09-12 (Mau, «APAGA LA PANTALLA VIEJA») — `pages/Ventas.tsx` se
            movió a `pages/_apagadas/`. Se vende desde la unidad, dentro de la casa del
            proyecto. Ver `RedirigirVentasApagada` arriba. */}
        <Route path="ventas"          element={<RedirigirPantallaApagada tab="unidades" aviso="Ahora se vende desde la unidad, dentro de cada proyecto. Te llevamos a Unidades del proyecto en el que estás." avisoSinProyecto="Ahora se vende desde la unidad, dentro de cada proyecto. Elige el proyecto para entrar." />} />
        {/* I-251: la ENTRADA de Obra salió del menú/riel/paleta, pero la pantalla se QUEDA montada (ORQ, 1-oct): hace lo que la casa
            aún no alcanza (Gantt completo, Parkinson, 4 tarjetas — bodega T12·P5) hasta que OBRA (tanda 2) lo mude. */}
        <Route path="obra"            element={<RequireModule modulo="obra"><LazyRoute><Obra /></LazyRoute></RequireModule>} />
        <Route path="workspace"       element={<RequireModule modulo="tareas"><LazyRoute><Workspace /></LazyRoute></RequireModule>} />
        <Route path="tareas"          element={<RequireModule modulo="tareas"><LazyRoute><Tareas /></LazyRoute></RequireModule>} />
        <Route path="chat"            element={<RequireModule modulo="tareas"><LazyRoute><Chat /></LazyRoute></RequireModule>} />
        {/* I-250: la pantalla suelta se apagó (Mau, 1-oct 13:59); Post-venta se muda a la casa del proyecto. */}
        <Route path="postventa"       element={<PostventaSeMudo />} />
        <Route path="reportes"        element={<RequireModule modulo="reportes"><LazyRoute><Reportes /></LazyRoute></RequireModule>} />
        <Route path="notificaciones"  element={<RequireModule modulo="notificaciones"><LazyRoute><Notificaciones /></LazyRoute></RequireModule>} />
        <Route path="configuracion"   element={<RequireModule modulo="configuracion"><LazyRoute><Configuracion /></LazyRoute></RequireModule>} />
        <Route path="overhead"        element={<RequireModule modulo="overhead"><LazyRoute><Overhead /></LazyRoute></RequireModule>} />
        <Route path="contabilidad"    element={<RequireModule modulo="contabilidad"><LazyRoute><Contabilidad /></LazyRoute></RequireModule>} />
        {/* 404 interno */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
};

export default App;
